import ChordButton from "@/components/ChordButton";
import ChordDiagram from "@/components/ChordDiagram";
import Modal from "@/components/Modal";
import useEventListener from "@/hooks/useEventListener";
import SpotifyFilled from "@/public/Spotify-Filled.svg";
import YouTubeFilled from "@/public/YouTube-Filled.svg";
import { useApp, useAppStore } from "@/store/app";
import { useReplicachePreferences } from "@/store/replicache";
import { instrumentLabels } from "@/utils/chordFingerPositions";
import type { Instrument } from "@/utils/types";
import cx from "classnames";
import Image from "next/image";
import { useRef, useState } from "react";
import {
  MdClose,
  MdKeyboardArrowUp,
  MdOutlineArrowDropDown,
  MdPlayCircleFilled,
} from "react-icons/md";
import shallow from "zustand/shallow";

const SongViewDrawer = ({
  chords,
  instrument = "guitar",
  isCollapsed,
  setIsCollapsed,
}: {
  chords: string[];
  instrument?: Instrument;
  isCollapsed: boolean;
  setIsCollapsed: (T: boolean) => void;
}) => {
  const { activeChord, setActiveChord } = useAppStore(
    ({ activeChord, setActiveChord }) => ({
      activeChord,
      setActiveChord,
    }),
    shallow,
  );
  const [type, setType] = useState<string>("chord");

  const toggleCollapsed = (type?: string) => {
    setType(type || "chord");
    setIsCollapsed(!isCollapsed);
    setActiveChord("");
  };

  return (
    <div className="bg-base-300 print:hidden">
      <div
        className={cx(
          "border-t-[1px] border-[#00000040] relative flex transition-all duration-250",
          isCollapsed ? "h-[52px]" : "h-[170px]",
        )}
      >
        {isCollapsed ? (
          <DrawerClosed chords={chords} toggleCollapsed={toggleCollapsed} />
        ) : (
          <DrawerOpen
            type={activeChord || !activeChord ? "chord" : type}
            chords={chords}
            instrument={instrument}
            toggleCollapsed={toggleCollapsed}
          />
        )}
      </div>
    </div>
  );
};

export default SongViewDrawer;

const DrawerOpen = ({
  chords,
  instrument,
  toggleCollapsed,
  type,
}: {
  chords: string[];
  instrument: Instrument;
  toggleCollapsed: () => void;
  type: string;
}) => {
  const LOG_TAG = "SongView DrawerOpen";
  const activeChord = useAppStore(({ activeChord }) => activeChord);
  const drawerRef = useRef<HTMLDivElement>(null);

  const chordsToShow = activeChord ? [activeChord] : chords;

  const { setPreferences } = useApp();
  const { preferences } = useReplicachePreferences();

  const handleClickInstrument = (key: Instrument) => {
    console.debug("handleClickInstrument", key);
    setPreferences.callback({
      ...preferences,
      x_instrument: key,
      updated_timestamp: Date.now(),
    });
  };

  const modalId = "song-view-drawer-modal";

  let content: React.ReactNode | string;
  switch (type) {
    case "chord":
      content = (
        <div className="flex w-full flex-col p-3">
          <div className="inline-flex">
            <label
              htmlFor={modalId}
              className="modal-button inline-flex items-center cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                //e.preventDefault();
              }}
            >
              {instrumentLabels[instrument]}
              <div className="btn btn-sm btn-ghost btn-circle text-2xl ml-1">
                <MdOutlineArrowDropDown />
              </div>
            </label>
          </div>

          <Modal id={modalId}>
            <h3 className="font-bold text-lg mb-4">Change Instrument</h3>
            {Object.keys(instrumentLabels).map((key) => (
              <div
                key={`${key}`}
                className="p-4 pl-6 border-t-[1px] border-[#ffffff40] cursor-pointer hover:bg-[#ffffff10] text-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClickInstrument(key as Instrument);
                }}
              >
                {instrumentLabels[key as Instrument]}
              </div>
            ))}
          </Modal>

          <div className="overflow-auto w-full">
            <div className="flex">
              {chordsToShow.map((chord: string) => (
                <ChordDiagram
                  key={chord}
                  chord={chord}
                  instrument={instrument}
                  activeChord={chord === activeChord}
                />
              ))}
            </div>
          </div>
        </div>
      );
      break;
    case "youtube":
      content = "Youtube video";
      break;
    case "spotify":
      content = "Spotify ";
      break;
    case "audio":
      content = "Audio";
      break;
    case "image":
      content = "Image";
      break;
    default:
      console.warn("No implementation for content", type);
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    //console.debug("SongViewDrawer handleClick", e.target, drawerRef.current);
    const target = e.target as HTMLElement;
    if (drawerRef.current?.contains(target)) {
      return;
    }
    if (
      target.classList.contains("modal-toggle") ||
      target.classList.contains("modal")
    ) {
      return;
    }
    toggleCollapsed();
    e.stopPropagation();
  };

  useEventListener("click", handleClick);

  return (
    <div ref={drawerRef} className="w-full">
      {content}
      <div
        className="btn btn-ghost btn-circle text-2xl absolute top-0 right-0"
        onClick={toggleCollapsed}
      >
        <MdClose />
      </div>
    </div>
  );
};

const DrawerClosed = function DrawerClosed({
  chords,
  toggleCollapsed,
}: { chords: string[]; toggleCollapsed: (T: string) => void }) {
  return (
    <div
      className="p-2 flex items-center w-full"
      onClick={(e) => {
        toggleCollapsed("chord");
        e.stopPropagation();
      }}
    >
      <div className="flex flex-1 items-center justify-between">
        <div>
          {chords.map((chord: string) => {
            return <ChordButton key={chord} chord={chord} isInSong />;
          })}
        </div>
        <div>
          <div className="btn btn-ghost btn-circle text-2xl btn-sm">
            <MdKeyboardArrowUp />
          </div>
        </div>
      </div>
      <div className="hidden border-l-[1px] border-[#00000040]">
        <div
          className="btn btn-ghost btn-circle text-xl"
          onClick={() => toggleCollapsed("youtube")}
        >
          <Image src={YouTubeFilled} alt="YouTube" />
        </div>
        <div
          className="btn btn-ghost btn-circle text-xl"
          onClick={() => toggleCollapsed("spotify")}
        >
          <Image src={SpotifyFilled} alt="Spotify" />
        </div>
        <div
          className="btn btn-ghost btn-circle text-xl"
          onClick={() => toggleCollapsed("audio")}
        >
          <MdPlayCircleFilled />
        </div>
        <div
          className="btn btn-ghost btn-circle text-xl"
          onClick={() => toggleCollapsed("image")}
        >
          <MdKeyboardArrowUp />
        </div>
      </div>
    </div>
  );
};
